import { useState, useMemo, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Wrench, Sparkles, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Fix Team Names modal — surfaces all roster teams + the raw ESPN/GotSport
 * names that didn't match anything (italic+grey rows on the Schedule view)
 * and lets the admin rename teams to match. Renames preserve team_id so
 * draft picks stay intact, AND auto-rebind any orphaned schedule rows.
 *
 * Usage: open from AdminPanel → Teams tab. Modal fetches /teams/unmatched
 * and renders a two-column layout — orphan ESPN names on the left, roster
 * teams on the right.
 */
export default function FixTeamNamesModal({ open, onOpenChange, leagueId, tournament, teams, onSaved }) {
    const [orphans, setOrphans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [drafts, setDrafts] = useState({}); // teamId -> in-progress edit

    // Detect which provider the tournament syncs from — used by the Refresh button.
    const syncProvider = useMemo(() => {
        if (!tournament) return null;
        if (tournament.gotsport_event_id) return "gotsport";
        const tpl = tournament.template;
        if (tpl === "world_cup" || tpl === "world_cup_knockout"
            || tpl === "march_madness" || tpl === "football_playoffs") return "espn";
        return null;
    }, [tournament]);

    const sortedTeams = useMemo(
        () => [...(teams || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
        [teams]
    );

    const fetchOrphans = useCallback(async () => {
        if (!open || !leagueId) return;
        setLoading(true);
        try {
            const r = await api.listUnmatchedTeams(leagueId);
            setOrphans(r.orphan_names || []);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not load unmatched names");
        } finally {
            setLoading(false);
        }
    }, [open, leagueId]);

    useEffect(() => { fetchOrphans(); }, [fetchOrphans]);

    const saveRename = async (teamId, newName) => {
        const clean = (newName || "").trim();
        if (!clean) {
            toast.error("Team name cannot be empty");
            return;
        }
        setSavingId(teamId);
        try {
            const r = await api.renameTeam(leagueId, teamId, clean);
            if (r.renamed === false) {
                toast.info("Name unchanged — nothing to update.");
            } else {
                const reboundMsg = r.scheduled_relinked > 0
                    ? ` and re-bound ${r.scheduled_relinked} schedule row${r.scheduled_relinked === 1 ? "" : "s"}`
                    : "";
                toast.success(`Renamed to "${r.new_name}"${reboundMsg}.`);
            }
            setDrafts(prev => { const c = { ...prev }; delete c[teamId]; return c; });
            await fetchOrphans();
            onSaved?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Rename failed");
        } finally {
            setSavingId(null);
        }
    };

    const applySuggested = async (orphan) => {
        if (!orphan.suggested_team_id) return;
        await saveRename(orphan.suggested_team_id, orphan.raw_name);
    };

    const handleRefreshSync = async () => {
        if (!syncProvider || !tournament?.id) return;
        setRefreshing(true);
        try {
            if (syncProvider === "espn") {
                const r = await api.syncEspn(tournament.id);
                toast.success(`ESPN sync complete (${r?.imported_count ?? 0} new, ${r?.updated_count ?? 0} updated).`);
            } else if (syncProvider === "gotsport") {
                const r = await api.gotsportRefresh(leagueId);
                toast.success(`GotSport refresh complete (${r?.matches_imported ?? 0} matches synced).`);
            }
            await fetchOrphans();
            onSaved?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Refresh failed");
        } finally {
            setRefreshing(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="fix-team-names-modal"
                className="bg-ink border-2 border-neon text-white max-w-3xl max-h-[90dvh] overflow-y-auto"
            >
                <DialogHeader>
                    <DialogTitle className="font-display font-extrabold text-xl uppercase flex items-center gap-2">
                        <Wrench className="w-5 h-5 text-neon" /> Fix Team Names
                    </DialogTitle>
                    <DialogDescription className="text-zinc-400 text-xs">
                        Schedule rows in <em className="text-zinc-500">italic grey</em> mean the team name from the sync provider
                        didn't match any team in your roster. Rename the matching roster team below — your draft picks stay intact
                        because the team ID doesn't change. Orphan schedule rows auto-rebind on save.
                    </DialogDescription>
                </DialogHeader>

                {/* Orphan list (top) */}
                <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500">
                            Unmatched names found on schedule
                        </p>
                        {syncProvider && (
                            <button
                                data-testid="refresh-sync-btn"
                                onClick={handleRefreshSync}
                                disabled={refreshing}
                                title={syncProvider === "espn"
                                    ? "Re-pull live data from ESPN and re-bind orphan rows"
                                    : "Re-pull schedule from GotSport and re-bind orphan rows"}
                                className="brutal-btn-ghost text-[10px] px-2 py-1 border-cyber text-cyber flex items-center gap-1.5 disabled:opacity-40"
                            >
                                <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
                                {refreshing ? "Refreshing…" : `Refresh ${syncProvider === "espn" ? "ESPN" : "GotSport"}`}
                            </button>
                        )}
                    </div>
                    {loading ? (
                        <p className="text-xs text-zinc-500">Scanning…</p>
                    ) : orphans.length === 0 ? (
                        <p className="text-xs text-neon flex items-center gap-1">
                            <Sparkles className="w-3.5 h-3.5" /> No unmatched names. The schedule should render clean.
                        </p>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {orphans.map(o => {
                                const suggestedTeam = sortedTeams.find(t => t.id === o.suggested_team_id);
                                return (
                                    <div
                                        key={o.raw_name}
                                        data-testid={`orphan-row-${o.raw_name.replace(/\s+/g, "-")}`}
                                        className="brutal-card-light p-2 border-zinc-700"
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="min-w-0">
                                                <p className="font-bold text-sm truncate text-zinc-300 italic">
                                                    "{o.raw_name}"
                                                </p>
                                                <p className="text-[10px] text-zinc-500">
                                                    {o.count} schedule row{o.count === 1 ? "" : "s"}
                                                </p>
                                            </div>
                                            {suggestedTeam && (
                                                <button
                                                    data-testid={`apply-suggestion-${o.raw_name.replace(/\s+/g, "-")}`}
                                                    onClick={() => applySuggested(o)}
                                                    disabled={savingId === o.suggested_team_id}
                                                    className="brutal-btn-ghost text-[10px] px-2 py-1 border-neon text-neon shrink-0"
                                                    title={`Rename "${suggestedTeam.name}" → "${o.raw_name}"`}
                                                >
                                                    {savingId === o.suggested_team_id ? "…" : `→ ${suggestedTeam.name}`}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Roster (bottom) — every team gets an editable name */}
                <div className="space-y-2 mt-4">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500">
                        All teams ({sortedTeams.length})
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[35dvh] overflow-y-auto pr-1">
                        {sortedTeams.map(t => {
                            const draftVal = drafts[t.id];
                            const currentValue = draftVal !== undefined ? draftVal : t.name;
                            const hasChange = draftVal !== undefined && draftVal.trim() !== "" && draftVal.trim() !== t.name;
                            return (
                                <div key={t.id} className={`brutal-card-light p-2 ${hasChange ? "border-cyber" : ""}`}>
                                    <div className="flex items-center gap-2">
                                        <span className="text-lg">{t.flag || "🏳️"}</span>
                                        <input
                                            data-testid={`team-rename-input-${t.id}`}
                                            type="text"
                                            value={currentValue}
                                            placeholder={t.name}
                                            onChange={e => setDrafts(p => ({ ...p, [t.id]: e.target.value }))}
                                            className="flex-1 bg-ink border-2 border-[#333] rounded px-2 py-1 text-sm focus:border-cyber focus:outline-none"
                                        />
                                        <button
                                            data-testid={`team-rename-save-${t.id}`}
                                            onClick={() => saveRename(t.id, currentValue)}
                                            disabled={!hasChange || savingId === t.id}
                                            className="brutal-btn-primary text-[10px] px-2 py-1 shrink-0 disabled:opacity-30"
                                        >
                                            {savingId === t.id ? "…" : "Save"}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        data-testid="fix-team-names-close-btn"
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        className="text-xs"
                    >
                        Close
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
